/**
 * Node converters: Legacy Format to React Flow
 * Main orchestrator for converting legacy node structures to React Flow node format
 */
import type { SwitchboardNode } from '../../shared/reactFlowTypes'
import { NodeType } from '../../shared/reactFlowTypes'
import type { LegacyFormatNode } from '../index'
import { parsePixelValue } from '../utils'
import {
  mapAiAgentV1Fields,
  mapAutoAttendantFields,
  mapCallDistributionFields,
  mapChangeCallerIdFields,
  mapConferenceRoomFields,
  mapDayNightFields,
  mapDialByNameFields,
  mapExternalTransferFields,
  mapFaxDetectFields,
  mapInteractiveWebhookFields,
  mapJumpToSwitchboardFields,
  mapLeaveVoicemailFields,
  mapQueueFields,
  mapScheduleFields,
  mapSmsNotificationFields,
  mapSoundClipFields,
  mapTransferRolloverFields,
  mapWaitFields,
  mapWebhookFields,
} from './fieldMappers'

/**
 * Convert a legacy format node to React Flow node format
 *
 * @param legacyNode - Legacy format node structure
 * @param nodeType - React Flow node type (or 'start' for start nodes)
 * @param kind - Legacy node kind string
 * @returns React Flow node with converted data and position
 */
export function convertLegacyNodeToReactFlowNode(
  legacyNode: LegacyFormatNode,
  nodeType: NodeType | 'start',
  kind: string,
): SwitchboardNode {
  const position = {
    x: parsePixelValue(legacyNode.left),
    y: parsePixelValue(legacyNode.top),
  }

  const nodeData: Record<string, unknown> = {
    label:
      kind === 'start_node'
        ? 'START'
        : legacyNode.kind.replace(/_/g, ' ').toUpperCase(),
  }

  let hasTimeout = false
  let isSmsNotification = false

  const html = legacyNode.html
  if (html) {
    Object.entries(html).forEach(([key, value]) => {
      nodeData[key] = value
    })

    if (
      'sms_number' in html ||
      'sms_recipient' in html ||
      ('message' in html &&
        typeof html.message === 'string' &&
        (html.message.includes('Missed call') ||
          html.message.includes('CALLER_ID')))
    ) {
      isSmsNotification = true
    }

    if ('extension_id' in html) {
      const extId = String(html.extension_id)
      nodeData.extensionId = extId === 'null' ? null : extId
    } else if ('line_id' in html) {
      const lineId = String(html.line_id)
      nodeData.extensionId = lineId === 'null' ? null : lineId
    }

    if ('queue_id' in html) {
      const qId = String(html.queue_id)
      nodeData.queueId = qId === 'null' ? null : qId
    }
    if ('timeout' in html) {
      const timeout = html.timeout
      const timeoutValue =
        typeof timeout === 'string'
          ? Number(timeout) || undefined
          : Number(timeout) || undefined
      nodeData.timeout = timeoutValue
      hasTimeout = timeoutValue !== undefined && timeoutValue !== null
    }

    // Apply all field mapping functions
    mapScheduleFields(legacyNode, nodeData, kind, nodeType, html)
    mapLeaveVoicemailFields(legacyNode, nodeData, kind, nodeType, html)
    mapExternalTransferFields(legacyNode, nodeData, kind, nodeType, html)
    mapAutoAttendantFields(legacyNode, nodeData, kind, nodeType, html)
    mapSmsNotificationFields(
      legacyNode,
      nodeData,
      kind,
      nodeType,
      isSmsNotification,
      html,
    )
    mapFaxDetectFields(legacyNode, nodeData, kind, nodeType, html)
    mapInteractiveWebhookFields(legacyNode, nodeData, kind, nodeType, html)
    mapWebhookFields(legacyNode, nodeData, kind, nodeType, html)
    mapChangeCallerIdFields(legacyNode, nodeData, kind, nodeType, html)
    mapWaitFields(legacyNode, nodeData, kind, nodeType, html)
    mapConferenceRoomFields(legacyNode, nodeData, kind, nodeType, html)
    mapJumpToSwitchboardFields(legacyNode, nodeData, kind, nodeType, html)
    mapDayNightFields(legacyNode, nodeData, kind, nodeType, html)
    mapAiAgentV1Fields(legacyNode, nodeData, kind, nodeType, html)
    mapCallDistributionFields(legacyNode, nodeData, kind, nodeType, html)
    mapSoundClipFields(legacyNode, nodeData, kind, nodeType, html)
    mapDialByNameFields(legacyNode, nodeData, kind, nodeType, html)
    mapQueueFields(legacyNode, nodeData, kind, nodeType, html)
    mapTransferRolloverFields(legacyNode, nodeData, kind, nodeType, html)
  }

  let finalType: NodeType | 'start' = nodeType

  if (isSmsNotification) {
    finalType = NodeType.SmsNotification
  } else if (
    (nodeType === NodeType.Transfer ||
      kind === 'transfer' ||
      kind === 'simple_dial') &&
    hasTimeout &&
    kind !== 'start_node'
  ) {
    finalType = NodeType.TransferAndContinue
  }

  if (kind === 'start_node') {
    finalType = 'start'
  }

  return {
    id: legacyNode.id,
    type: finalType,
    position,
    data: nodeData,
  } as SwitchboardNode
}
